import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import { toast } from "@4pace-cpq/core";
import { useGetByIdQuotes } from "@4pace-cpq/core/api";
import { Tooltip } from "@mui/material";
import Box from "@mui/material/Box";

import { iframeId, language } from "@/Router";
import CustomDialog from "@/components/CustomDialog/CustomDialog";
import CustomDialogActions from "@/components/CustomDialog/CustomDialogActions";
import { RegioluxMessageIcon } from "@/icons/RegioluxIcons";
import {
  useConfigurationServiceGetCustomApiConfigurationPublicShoppingCartByQuoteIdExtendedData,
  useConfigurationServicePostCustomApiConfigurationPublicShoppingCartQuoteRequestMail,
} from "@/openapi/api-cpq/queries/queries";

import { UserInformationForm } from "./UserInformationForm";

export function RequestQuoteDialog({
  quoteId,
  isEmpty = true,
}: {
  quoteId: string;
  isEmpty?: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  const { data: extendedData } =
    useConfigurationServiceGetCustomApiConfigurationPublicShoppingCartByQuoteIdExtendedData(
      { quoteId },
    );

  const { data: quoteData } = useGetByIdQuotes(
    {
      path: { id: quoteId },
      query: { Includes: "PropertySet.Properties.Translations.Language" },
    },
    undefined,
    { enabled: !!quoteId },
  );

  const geolocationId = quoteData?.propertySet?.properties?.find(
    (property) => property.identifier === "QuoteCreationGeolocation",
  );

  const isGeolocationDE = geolocationId?.value === "DE";

  const projectName = extendedData?.projectName;
  const projectLocation = extendedData?.projectLocation;

  const [specialistRetailerName, setSpecialistRetailerName] = useState("");
  const [specialistRetailerLocation, setSpecialistRetailerLocation] =
    useState("");
  const [specialistRetailerContact, setSpecialistRetailerContact] =
    useState("");
  const [specialistRetailerCustomerNo, setSpecialistRetailerCustomerNo] =
    useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [street, setStreet] = useState("");
  const [postcode, setPostcode] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [position, setPosition] = useState("");
  const [industry, setIndustry] = useState("");
  const [country, setCountry] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [dataPrivacy, setDataPrivacy] = useState(false);

  const userInformationProps = {
    specialistRetailerName,
    specialistRetailerLocation,
    specialistRetailerContact,
    specialistRetailerCustomerNo,
    message,
    email,
    title,
    firstName,
    lastName,
    phoneNumber,
    street,
    postcode,
    companyName,
    position,
    industry,
    country,
    mobileNumber,
    dataPrivacy,
    setSpecialistRetailerName,
    setSpecialistRetailerLocation,
    setSpecialistRetailerContact,
    setSpecialistRetailerCustomerNo,
    setMessage,
    setEmail,
    setTitle,
    setFirstName,
    setLastName,
    setPhoneNumber,
    setStreet,
    setPostcode,
    setCompanyName,
    setPosition,
    setIndustry,
    setCountry,
    setMobileNumber,
    setDataPrivacy,
  };

  function resetInputFields(): void {
    setSpecialistRetailerName("");
    setSpecialistRetailerLocation("");
    setSpecialistRetailerContact("");
    setSpecialistRetailerCustomerNo("");
    setMessage("");
    setEmail("");
    setTitle("");
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setStreet("");
    setPostcode("");
    setCompanyName("");
    setPosition("");
    setIndustry("");
    setCountry("");
    setMobileNumber("");
    setDataPrivacy(false);
  }

  const { mutate: requestQuote } =
    useConfigurationServicePostCustomApiConfigurationPublicShoppingCartQuoteRequestMail();

  const userSessionId = window.sessionStorage.getItem("userSessionId");

  function onConfirm(closeDialog: () => void): void {
    if (userSessionId) {
      requestQuote(
        {
          requestBody: {
            localeId: language ? language : "de-DE",
            userSessionId,
            title,
            firstName,
            lastName,
            position,
            phoneNumber,
            mobileNumber,
            companyName,
            industry,
            street,
            city: postcode,
            country,
            message,
            emailAddress: email,
            specialistRetailerName,
            specialistRetailerLocation,
            specialistRetailerContact,
            specialistRetailerCustomerNo,
          },
        },
        {
          onSuccess: () => {
            const postMessage = {
              iframeId: iframeId,
              eventName: "clearShoppingCart",
            };
            window.parent.postMessage(postMessage, "*");
            resetInputFields();
            closeDialog();
          },
          onError: (error) => {
            console.log(error);
            toast.error(t("regiolux:toast.requestQuote.error"));
          },
        },
      );
    } else {
      console.error("User session id not found");
    }
  }

  const re = /\S+@\S+\.\S+/;

  return (
    <Box>
      <Tooltip
        title={
          projectName === "" || projectLocation === ""
            ? t("regiolux:dialog.requestQuote.text.tooltip")
            : ""
        }
      >
        <span>
          <CustomDialog
            dialog={{
              title: t("regiolux:dialog.requestQuote.text.title"),
              description: t("regiolux:dialog.requestQuote.description"),
              content: (
                <UserInformationForm
                  {...userInformationProps}
                  isGeolocationDE={isGeolocationDE}
                />
              ),
              actions: ({ closeDialog }) => (
                <CustomDialogActions
                  textCancel={t("regiolux:dialog.requestQuote.text.cancel")}
                  textConfirm={t("regiolux:dialog.requestQuote.text.confirm")}
                  confirmDisabled={
                    email === "" ||
                    !re.test(email) ||
                    !dataPrivacy ||
                    !firstName ||
                    !lastName ||
                    !specialistRetailerName ||
                    !specialistRetailerLocation
                  }
                  onCancel={() => {
                    closeDialog();
                  }}
                  onConfirm={() => {
                    onConfirm(closeDialog);
                  }}
                />
              ),
            }}
            buttonProps={{
              disabled: projectName === "" || projectLocation === "" || isEmpty,
              children: t("regiolux:dialog.requestQuote.open"),
              startIcon: <RegioluxMessageIcon />,
            }}
          />
        </span>
      </Tooltip>
    </Box>
  );
}
